import { humanize } from '../../../ui/Badge';
import { ExternalEvidence } from '../ExternalEvidence';
import { CONTEXT_SECTION_TITLES, type ContextSection } from '@lixionary-jira/domain';
import { Badge } from '../../../ui/Badge';
import { Markdown } from '../../../ui/Markdown';
import { Card, KeyValue } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import type { ReleaseDetail } from '../types';

const external = { target: '_blank', rel: 'noopener noreferrer nofollow' } as const;
export function Candidate({ r }: { r: ReleaseDetail }) {
  const b = r.candidate!.binding;
  return (
    <Card
      title={`Candidate ${r.candidate!.number}`}
      subtitle={
        <>
          Binding <span className="mono">{r.candidate!.bindingHash.slice(0, 12)}</span> ·{' '}
          <Timestamp value={r.candidate!.createdAt} />
        </>
      }
    >
      <KeyValue
        items={[
          ['Delivery method', humanize(b.deliveryMethod ?? 'github_workflow')],
          ['Reason', b.deliveryReason ?? 'Standard workflow'],
          [
            'Environment',
            <span key="e">
              {b.environmentName}{' '}
              {b.isCompletionTarget ? (
                <Badge tone="success" dot={false}>
                  Completion target
                </Badge>
              ) : (
                <Badge dot={false}>Not completion</Badge>
              )}
            </span>,
          ],
          [
            'Revision',
            b.sourceRevision ? (
              <span key="r" className="mono">
                {b.sourceRevision}
              </span>
            ) : (
              <span key="r" className="muted">
                Frozen from {b.deployRef ?? 'the default branch'} when the release proceeds
              </span>
            ),
          ],
          [
            'CI',
            b.ciChecks ? (
              b.ciChecks.length ? (
                `Passed: ${b.ciChecks.join(', ')}`
              ) : (
                'No checks reported'
              )
            ) : (
              <span key="ci" className="muted">
                Checked when the release proceeds
                {r.environment.requiredChecks.length
                  ? `; requires ${r.environment.requiredChecks.join(', ')}`
                  : ''}
              </span>
            ),
          ],
          [
            'Deploys with',
            b.deliveryMethod && b.deliveryMethod !== 'github_workflow' ? (
              humanize(b.deliveryMethod)
            ) : b.deployWorkflow ? (
              <span key="w" className="mono small">
                {b.deployWorkflow} on {b.deployRef}
              </span>
            ) : (
              '—'
            ),
          ],
          [
            'Artifact',
            b.artifactDigest ? (
              <span className="mono small">{b.artifactDigest}</span>
            ) : (
              'Source revision binding'
            ),
          ],
          [
            'Repository',
            <span key="rp" className="mono small">
              {b.repositoryUrl ?? '—'}
            </span>,
          ],

          [
            'Context',
            Object.entries(r.candidate!.snapshot?.contextVersions ?? {})
              .map(([s, n]) => `${CONTEXT_SECTION_TITLES[s as ContextSection]} ${n ? `v${n}` : '—'}`)
              .join(' · '),
          ],
          ['Exceptions', b.exceptions?.length ? b.exceptions.join(', ') : 'None'],
        ]}
      />
      {b.externalDeployment && <ExternalEvidence evidence={b.externalDeployment} />}
      {b.mergedPullRequests && (
        <>
          <span className="small strong">Merged pull requests in this revision</span>
          <ul className="checklist">
            {b.mergedPullRequests.map((p) => (
              <li key={`${p.key}-${p.number}`}>
                <span className="mono">{p.key}</span>
                <a href={p.url} {...external}>
                  #{p.number}
                </a>
                <span className="mono small muted">{p.mergeCommitSha.slice(0, 12)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {b.integrationEvidence && (
        <>
          <span className="small strong">Integration notes</span>
          <Markdown>{b.integrationEvidence}</Markdown>
        </>
      )}
      <span className="small strong">Deployment plan</span>
      <Markdown>{b.deploymentPlan ?? ''}</Markdown>
      <span className="small strong">Required checks</span>
      <ul className="checklist">
        {b.verificationChecks.map((c) => (
          <li key={c}>
            {c}
            {b.manualChecks?.includes(c) && (
              <span className="small muted"> (manual, recorded after the deploy)</span>
            )}
          </li>
        ))}
      </ul>
      <span className="small strong">Rollback plan</span>
      <Markdown>{b.rollbackPlan ?? ''}</Markdown>
    </Card>
  );
}
